import { Header } from "@/components/layout/Header";
import { Sidebar } from "@/components/layout/Sidebar";
import { Helmet } from "react-helmet-async";
import { FileText, Shield, AlertTriangle, Scale, Ban, DollarSign } from "lucide-react";

export default function Terms() {
  return (
    <>
      <Helmet>
        <title>Terms of Service - TheMoonRun</title>
        <meta name="description" content="TheMoonRun Terms of Service. Please read carefully before using our platform." />
      </Helmet>
      
      <div className="min-h-screen bg-background">
        <Header />
        
        <div className="flex">
          <Sidebar />
          
          <main className="flex-1 min-w-0 p-4 lg:p-6">
            <div className="max-w-4xl mx-auto">
              {/* Header */}
              <div className="flex items-center gap-4 mb-8">
                <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center">
                  <FileText className="w-7 h-7 text-primary" />
                </div>
                <div>
                  <h1 className="text-2xl font-display font-bold text-foreground">Terms of Service</h1>
                  <p className="text-muted-foreground">Last updated: December 2024</p>
                </div>
              </div>

              <div className="p-6 rounded-xl bg-card border border-border mb-8">
                <div className="flex items-start gap-3 text-warning">
                  <AlertTriangle className="w-5 h-5 mt-0.5 flex-shrink-0" />
                  <p className="text-sm">
                    <strong>Important:</strong> By using TheMoonRun, you acknowledge that you understand the risks of cryptocurrency trading. 
                    Meme coins are highly speculative and you may lose your entire investment. Trade responsibly.
                  </p>
                </div>
              </div>

              <div className="prose prose-invert max-w-none space-y-8">
                <section className="p-6 rounded-xl bg-card border border-border">
                  <div className="flex items-center gap-3 mb-4">
                    <Scale className="w-5 h-5 text-primary" />
                    <h2 className="text-xl font-semibold m-0">1. Acceptance of Terms</h2>
                  </div>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    By accessing or using TheMoonRun ("Platform"), you agree to be bound by these Terms of Service. 
                    If you do not agree to all terms, you may not use the Platform. We reserve the right to modify 
                    these terms at any time. Continued use after changes constitutes acceptance of new terms.
                  </p>
                </section>

                <section className="p-6 rounded-xl bg-card border border-border">
                  <div className="flex items-center gap-3 mb-4">
                    <Shield className="w-5 h-5 text-primary" />
                    <h2 className="text-xl font-semibold m-0">2. Eligibility</h2>
                  </div>
                  <ul className="text-muted-foreground text-sm space-y-2 list-disc list-inside">
                    <li>You must be at least 18 years old to use this Platform</li>
                    <li>You must have legal capacity to enter into binding agreements</li>
                    <li>You must not be located in a jurisdiction where cryptocurrency trading is prohibited</li>
                    <li>You are responsible for compliance with your local laws and regulations</li>
                    <li>US persons are prohibited from using this Platform</li>
                  </ul>
                </section>

                <section className="p-6 rounded-xl bg-card border border-border">
                  <div className="flex items-center gap-3 mb-4">
                    <DollarSign className="w-5 h-5 text-primary" />
                    <h2 className="text-xl font-semibold m-0">3. Trading & Fees</h2>
                  </div>
                  <ul className="text-muted-foreground text-sm space-y-2 list-disc list-inside">
                    <li>All trades are final and irreversible once confirmed on the blockchain</li>
                    <li>Platform charges a 1% fee on all trades</li>
                    <li>Token launch fee is 0.5 SOL (non-refundable)</li>
                    <li>Priority fees are optional and go to Solana validators</li>
                    <li>We do not guarantee any returns or profits</li>
                    <li>Prices are determined by bonding curves and market activity</li>
                  </ul>
                </section>

                <section className="p-6 rounded-xl bg-card border border-border">
                  <div className="flex items-center gap-3 mb-4">
                    <Ban className="w-5 h-5 text-destructive" />
                    <h2 className="text-xl font-semibold m-0">4. Prohibited Activities</h2>
                  </div>
                  <p className="text-muted-foreground text-sm mb-3">The following activities are strictly prohibited:</p>
                  <ul className="text-muted-foreground text-sm space-y-2 list-disc list-inside">
                    <li>Market manipulation, wash trading, or artificial volume inflation</li>
                    <li>Creating tokens that impersonate existing projects or brands</li>
                    <li>Launching scam tokens or rug pulls</li>
                    <li>Harassing, threatening, or doxxing other users</li>
                    <li>Using bots or automated systems to gain unfair advantage</li>
                    <li>Circumventing platform security or rate limits</li>
                    <li>Money laundering or financing illegal activities</li>
                    <li>Spreading false information about tokens or the platform</li>
                  </ul>
                  <p className="text-destructive text-sm mt-4 font-medium">
                    Violation of these terms will result in immediate account suspension and potential legal action.
                  </p>
                </section>

                <section className="p-6 rounded-xl bg-card border border-border">
                  <div className="flex items-center gap-3 mb-4">
                    <AlertTriangle className="w-5 h-5 text-warning" />
                    <h2 className="text-xl font-semibold m-0">5. Risk Disclaimer</h2>
                  </div>
                  <ul className="text-muted-foreground text-sm space-y-2 list-disc list-inside">
                    <li>Cryptocurrency trading involves substantial risk of loss</li>
                    <li>Meme coins are highly volatile and speculative</li>
                    <li>Past performance does not guarantee future results</li>
                    <li>You may lose 100% of your investment</li>
                    <li>We are not financial advisors and do not provide investment advice</li>
                    <li>DYOR (Do Your Own Research) before trading</li>
                  </ul>
                </section>

                <section className="p-6 rounded-xl bg-card border border-border">
                  <h2 className="text-xl font-semibold mb-4">6. Token Locking System</h2>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    The Platform implements a community-driven token locking system. Tokens can be locked by majority 
                    community vote if suspected of being scams or violating terms. Locked tokens cannot be traded until 
                    unlocked by subsequent vote. The Platform reserves the right to lock any token at its sole discretion 
                    to protect users. Token creators have no recourse against locking decisions.
                  </p>
                </section>

                <section className="p-6 rounded-xl bg-card border border-border">
                  <h2 className="text-xl font-semibold mb-4">7. Streamer Terms</h2>
                  <ul className="text-muted-foreground text-sm space-y-2 list-disc list-inside">
                    <li>Verified streamers receive vote multipliers as platform rewards</li>
                    <li>Streamers must disclose any token holdings when discussing tokens</li>
                    <li>Pump-and-dump schemes or coordinated manipulation is prohibited</li>
                    <li>Streamer status can be revoked for violations</li>
                    <li>Platform is not responsible for streamer recommendations</li>
                  </ul>
                </section>

                <section className="p-6 rounded-xl bg-card border border-border">
                  <h2 className="text-xl font-semibold mb-4">8. Limitation of Liability</h2>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    THE PLATFORM IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND. WE ARE NOT LIABLE FOR ANY 
                    DIRECT, INDIRECT, INCIDENTAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING FROM YOUR USE OF 
                    THE PLATFORM. OUR TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT OF FEES YOU PAID IN THE PAST 
                    12 MONTHS. SOME JURISDICTIONS DO NOT ALLOW LIMITATION OF LIABILITY, SO THESE LIMITATIONS 
                    MAY NOT APPLY TO YOU.
                  </p>
                </section>

                <section className="p-6 rounded-xl bg-card border border-border">
                  <h2 className="text-xl font-semibold mb-4">9. Indemnification</h2>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    You agree to indemnify and hold harmless TheMoonRun, its affiliates, officers, directors, 
                    employees, and agents from any claims, damages, losses, or expenses arising from your use 
                    of the Platform, violation of these terms, or infringement of any third-party rights.
                  </p>
                </section>

                <section className="p-6 rounded-xl bg-card border border-border">
                  <h2 className="text-xl font-semibold mb-4">10. Governing Law</h2>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    These terms shall be governed by and construed in accordance with the laws of the British 
                    Virgin Islands, without regard to its conflict of law provisions. Any disputes shall be 
                    resolved through binding arbitration in accordance with the rules of the International 
                    Chamber of Commerce.
                  </p>
                </section>

                <section className="p-6 rounded-xl bg-card border border-border">
                  <h2 className="text-xl font-semibold mb-4">11. Contact</h2>
                  <p className="text-muted-foreground text-sm">
                    For questions about these Terms, contact us at:{" "}
                    <a href="mailto:legal@themoon.run" className="text-primary hover:underline">legal@themoon.run</a>
                  </p>
                </section>
              </div>
            </div>
          </main>
        </div>
      </div>
    </>
  );
}
